/**
 * What a client says when a dashboard API call fails — read by BOTH apps (#2839).
 *
 * WHY THIS IS SHARED RATHER THAN A CONSTANT IN EACH APP: the same stale sign-in
 * was saying three different things at once. Mobile chat said "Your sign-in
 * expired…" (#2838), the web chat pin surface echoed the server and said
 * "Session expired - please sign in again", and the mobile RSVP and
 * private-lesson screens echoed the server too and said the literal string
 * **"Invalid authentication token"** — developer jargon in front of a coach,
 * on the wrong side of the CLAUDE.md UI Copy rule.
 *
 * THE RULE THIS EXISTS TO ENFORCE: a client owns its user-facing copy. Never
 * render `body.error` from an API response. That field is written for a
 * developer reading a log, it changes when a route is refactored, and it varies
 * by which auth helper the route happens to call — `validateAuthentication`
 * answers "Invalid authentication token", while `/api/organization/users`
 * answers "Unauthorized" for the identical situation. Neither is a sentence a
 * gym owner can act on.
 *
 * `apiErrorCopy()` is the minimal intervention: it intercepts ONLY the status
 * where the server's text is both useless and actively misleading, and leaves
 * every other response exactly as the caller had it. That is deliberate —
 * some routes return genuinely useful validation messages, and a blanket
 * replacement would throw those away.
 */

/**
 * Shown when the sign-in has gone stale. Named as a constant because it is the
 * SAME sentence on web and on mobile — see the header. If you are about to
 * write a second version of this, that is the bug.
 *
 * "Sign out and back in" rather than "your session expired": session is not a
 * word gym staff use, and it does not tell them what to do. This sentence names
 * the cause and the one action that fixes it, and it is true on both surfaces
 * (a stale cookie on the web and a stale token on the phone are both cleared by
 * signing out and back in).
 */
export const SESSION_EXPIRED =
  'Your sign-in expired. Please sign out and back in, then try again.';

/**
 * Pick the sentence to show for a failed API response.
 *
 * @param status      HTTP status from the response.
 * @param serverError `body.error` if the caller already parsed one. Used ONLY
 *                    for statuses this function does not own, preserving
 *                    whatever the caller did before.
 * @param fallback    The caller's own copy, for when there is no server text.
 *
 * A 401 always wins over `serverError`, because that is the case where the
 * server's text is guaranteed to be jargon and the retry the caller's fallback
 * usually suggests can never succeed.
 */
export function apiErrorCopy(
  status: number,
  serverError: string | null | undefined,
  fallback: string
): string {
  if (status === 401) return SESSION_EXPIRED;
  return serverError || fallback;
}
